import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { AlertCircle } from 'lucide-react';
import {
  type ResourceSummary,
  useMonthlyUsageSummary,
  useUsageSnapshots,
} from '@/hooks/useBilling';
import SubscriptionManagement from '@/components/SubscriptionManagement';
import PaymentMethodsManagement from '@/components/PaymentMethodsManagement';
import { PageContainer } from '@/components/ui/page-container';
import { PageHeader } from '@/components/ui/page-header';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress.tsx';
import {
  ResponsiveTable,
  type ResponsiveTableColumn,
} from '@/components/ui/responsive-table';
import { cn } from '@/lib/utils';

/**
 * Billing — usage summary + recharts BarChart per resource type.
 * Spec §4 MF. Migrated wrapper; the two delegated sub-components
 * (SubscriptionManagement + PaymentMethodsManagement) stay on Bulma
 * for now — they're sizable forms with Stripe Elements that need
 * their own migration commits.
 *
 * Sections (top → bottom):
 *   1. PageHeader
 *   2. SubscriptionManagement (Bulma, unchanged)
 *   3. PaymentMethodsManagement (Bulma, unchanged)
 *   4. Monthly summary — KPI grid + billing-period progress +
 *      resource-usage breakdown ResponsiveTable
 *   5. Daily cost charts — recharts BarChart per resource type, with
 *      Total / Average / Peak KPI strip below each
 *
 * Charts use design-token colors (primary / muted-foreground) so they
 * flip with dark mode. Tooltip and Y-axis tick formatters preserve
 * USD formatting from the original.
 */

interface DailyCostData {
  date: string;
  cost: number;
}

function formatCurrency(amount: string | number): string {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(num);
}

function formatDateOnly(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

export default function Billing() {
  const {
    data: monthlySummary,
    isLoading: isLoadingMonthly,
    error: monthlyError,
  } = useMonthlyUsageSummary();
  const {
    data: hourlySnapshots,
    isLoading: isLoadingHourly,
    error: hourlyError,
  } = useUsageSnapshots(undefined, !!monthlySummary);

  // Aggregate hourly snapshots into daily totals per resource type.
  // Same algorithm as the Bulma original — just inlined.
  const chartDataByResource = useMemo<Record<string, DailyCostData[]>>(() => {
    if (!hourlySnapshots || hourlySnapshots.length === 0) return {};
    const dailyData: Record<string, Record<string, number>> = {};
    hourlySnapshots.forEach((snapshot) => {
      const date = new Date(snapshot.snapshot_hour).toISOString().split('T')[0];
      const cost = parseFloat(snapshot.billable_amount);
      dailyData[snapshot.resource_type] ??= {};
      dailyData[snapshot.resource_type][date] =
        (dailyData[snapshot.resource_type][date] ?? 0) + cost;
    });
    const grouped: Record<string, DailyCostData[]> = {};
    Object.entries(dailyData).forEach(([resourceType, dates]) => {
      grouped[resourceType] = Object.entries(dates)
        .map(([date, cost]) => ({ date, cost: parseFloat(cost.toFixed(4)) }))
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    });
    return grouped;
  }, [hourlySnapshots]);

  const resourceColumns = useMemo<readonly ResponsiveTableColumn<ResourceSummary>[]>(
    () => [
      {
        key: 'resource_type',
        label: 'Resource',
        hideMobileLabel: true,
        render: (r) => <span className="font-medium capitalize">{r.resource_type}</span>,
      },
      {
        key: 'licensed_quantity',
        label: 'Included',
        render: (r) => <span className="font-mono">{r.licensed_quantity}</span>,
        align: 'right',
      },
      {
        key: 'peak_usage',
        label: 'Peak',
        render: (r) => (
          <span
            className={cn(
              'font-mono',
              r.peak_usage > r.licensed_quantity && 'text-sev-2 font-medium',
            )}
          >
            {r.peak_usage}
          </span>
        ),
        align: 'right',
      },
      {
        key: 'average_usage',
        label: 'Average',
        render: (r) => (
          <span className="font-mono text-muted-foreground">
            {r.average_usage.toFixed(2)}
          </span>
        ),
        align: 'right',
        hideAt: 'mobile',
      },
      {
        key: 'total_billable_amount',
        label: 'Cost',
        render: (r) => (
          <span className="font-mono font-medium">
            {formatCurrency(r.total_billable_amount)}
          </span>
        ),
        align: 'right',
      },
    ],
    [],
  );

  if (monthlyError ?? hourlyError) {
    return (
      <PageContainer width="medium">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>Error loading billing data:</strong>{' '}
            {(monthlyError ?? hourlyError)?.message}
          </AlertDescription>
        </Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer width="medium" className="space-y-6 md:space-y-8">
      <PageHeader
        title="Billing & usage"
        subtitle="Monitor your resource usage and costs"
      />

      {/* Bulma sub-components — embedded as-is. Migration lands separately. */}
      <SubscriptionManagement />
      <PaymentMethodsManagement />

      {/* Monthly summary */}
      <section className="bg-card border border-border rounded-lg p-5 md:p-6">
        <h2 className="text-base font-semibold mb-4">Monthly summary</h2>

        {isLoadingMonthly ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Skeleton className="h-20" />
            <Skeleton className="h-20" />
            <Skeleton className="h-20" />
          </div>
        ) : monthlySummary ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
              <KpiCard label="Month" value={monthlySummary.month} />
              <KpiCard
                label="Current cost"
                value={formatCurrency(monthlySummary.current_monthly_cost)}
              />
              {monthlySummary.is_current_month ? (
                <KpiCard
                  label="Projected monthly"
                  value={formatCurrency(monthlySummary.estimated_monthly_cost)}
                  hint="Based on current usage trends"
                  accent
                />
              ) : null}
            </div>

            {monthlySummary.is_current_month ? (
              <BillingPeriodProgress
                start={monthlySummary.billing_period_start}
                end={monthlySummary.billing_period_end}
              />
            ) : null}

            <h3 className="text-sm font-semibold mt-6 mb-3">Resource usage breakdown</h3>
            <ResponsiveTable<ResourceSummary>
              columns={resourceColumns}
              rows={monthlySummary.resource_summaries}
              rowKey={(r) => r.resource_type}
            />
          </>
        ) : null}
      </section>

      {/* Per-resource daily cost charts */}
      <section className="bg-card border border-border rounded-lg p-5 md:p-6">
        <h2 className="text-base font-semibold">Daily cost breakdown</h2>
        <p className="text-sm text-muted-foreground mt-1 mb-5">
          Track your daily costs by resource type throughout the month.
        </p>

        {isLoadingHourly ? (
          <Skeleton className="h-[300px] w-full" />
        ) : Object.keys(chartDataByResource).length > 0 ? (
          <div className="space-y-8">
            {Object.entries(chartDataByResource).map(([resourceType, data]) => (
              <ResourceChart key={resourceType} resourceType={resourceType} data={data} />
            ))}
          </div>
        ) : (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              No usage data available yet. Data will appear as your gateways report usage.
            </AlertDescription>
          </Alert>
        )}
      </section>
    </PageContainer>
  );
}

// ─── KPI tile ─────────────────────────────────────────────────────────

interface KpiCardProps {
  label: string;
  value: string;
  hint?: string;
  /** Highlight as the "projected" or "primary" KPI. */
  accent?: boolean;
}

function KpiCard({ label, value, hint, accent }: KpiCardProps) {
  return (
    <div
      className={cn(
        'rounded-md border p-4',
        accent ? 'border-primary/30 bg-brand-soft/40' : 'border-border bg-bg-subtle',
      )}
    >
      <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="text-lg font-semibold font-mono mt-1 truncate">{value}</div>
      {hint ? (
        <div className="text-xs text-muted-foreground mt-1">{hint}</div>
      ) : null}
    </div>
  );
}

// ─── Billing-period progress ──────────────────────────────────────────

function BillingPeriodProgress({ start, end }: { start: string; end: string }) {
  const now = new Date();
  const periodStart = new Date(start);
  const periodEnd = new Date(end);
  const totalDuration = periodEnd.getTime() - periodStart.getTime();
  const elapsed = now.getTime() - periodStart.getTime();
  const progressPercent = Math.round((elapsed / totalDuration) * 100);
  const daysRemaining = Math.ceil(
    (periodEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
  );

  return (
    <div className="rounded-md border border-border bg-bg-subtle p-4">
      <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-2">
        Billing period
      </div>
      <Progress value={progressPercent} className="h-2" />
      <div className="text-xs text-muted-foreground mt-2">
        {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} remaining in billing cycle
      </div>
    </div>
  );
}

// ─── Per-resource bar chart + KPI strip ───────────────────────────────

interface ResourceChartProps {
  resourceType: string;
  data: DailyCostData[];
}

function ResourceChart({ resourceType, data }: ResourceChartProps) {
  const totalCost = data.reduce((sum, day) => sum + day.cost, 0);
  const averageDailyCost = data.length > 0 ? totalCost / data.length : 0;
  const peakDailyCost = data.length > 0 ? Math.max(...data.map((d) => d.cost)) : 0;

  return (
    <div>
      <h3 className="text-sm font-semibold mb-3 capitalize">{resourceType} cost</h3>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-subtle)" />
          <XAxis
            dataKey="date"
            tickFormatter={formatDateOnly}
            angle={-45}
            textAnchor="end"
            height={60}
            stroke="var(--fg-subtle)"
            fontSize={11}
          />
          <YAxis
            tickFormatter={(value: number) => `$${value.toFixed(2)}`}
            stroke="var(--fg-subtle)"
            fontSize={11}
          />
          <Tooltip
            cursor={{ fill: 'var(--accent)', opacity: 0.4 }}
            contentStyle={{
              backgroundColor: 'var(--popover)',
              border: '1px solid var(--border)',
              borderRadius: '6px',
              color: 'var(--popover-foreground)',
              fontSize: '12px',
            }}
            labelFormatter={(label) => (typeof label === 'string' ? formatDateOnly(label) : label)}
            formatter={(value) => [typeof value === 'number' ? formatCurrency(value) : '', 'Daily cost']}
          />
          <Legend formatter={() => 'Daily cost'} wrapperStyle={{ fontSize: '12px' }} />
          <Bar dataKey="cost" fill="var(--primary)" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>

      <div className="grid grid-cols-3 gap-2 mt-3">
        <KpiCard label="Total cost" value={formatCurrency(totalCost)} accent />
        <KpiCard label="Average daily" value={formatCurrency(averageDailyCost)} />
        <KpiCard label="Peak daily" value={formatCurrency(peakDailyCost)} />
      </div>
    </div>
  );
}
